import { Component, Input, OnInit } from '@angular/core';
import { Validators, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Client, ProductGroup } from '@app/core/models/client';
import { NewFeedTransitionService } from '@app/core/services/new-feed-transition.service';
import { CharacterLimits } from '@app/core/utils/characterLimits';
import { CustomValidators } from '@app/core/utils/custom-validators';
import { URLUtils } from '@app/core/utils/url-utils';
import { debounceTime, Observable, of, tap } from 'rxjs';
import * as _ from 'lodash';
import { FeatureService } from '@app/core/services/feature.service';
import { SmsLanguageConfigService } from '@app/core/services/sms-language-config.service';
import { ClientHeaderComponent } from '../../../../shared/client-header/client-header.component';
import { TextInputComponent } from '../../../../shared/text-input/text-input.component';
import { InputInfoBoxComponent } from '../../../../shared/input-info-box/input-info-box.component';
import { FormInputStateControlComponent } from '../../../../shared/form-input-state-control/form-input-state-control.component';
import { HelpTextComponent } from './help-text/help-text.component';
import { ContactInfoInputsComponent } from './contact-info-inputs/contact-info-inputs.component';
import { ImageInputComponent } from '../../../image-input/image-input.component';
import { RadioBtnComponent } from '../../../../shared/radio-btn/radio-btn.component';
import { ColorPickerComponent } from '../../../../shared/color-picker/color-picker.component';
import { CheckboxComponent } from '../../../../shared/checkbox/checkbox.component';
import { NewGenericPhoneComponent } from '../../../../shared/new-generic-phone/new-generic-phone.component';
import { WireHeaderComponent } from '../../../../shared/wire-header/wire-header.component';
import { NetworkComponent } from '../../../../shared/phone/feed-preview/network.component';
import { ProductGroupPreviewReactContainerComponent } from '../product-group-preview-react-container/product-group-preview-react-container.component';

@Component({
    selector: 'app-new-product-info',
    templateUrl: './product-info.component.html',
    styleUrls: ['./product-info.component.scss'],
    standalone: true,
    imports: [
        ClientHeaderComponent,
        TextInputComponent,
        InputInfoBoxComponent,
        FormInputStateControlComponent,
        FormsModule,
        ReactiveFormsModule,
        HelpTextComponent,
        ContactInfoInputsComponent,
        ImageInputComponent,
        RadioBtnComponent,
        ColorPickerComponent,
        CheckboxComponent,
        NewGenericPhoneComponent,
        WireHeaderComponent,
        NetworkComponent,
        ProductGroupPreviewReactContainerComponent,
    ],
})
export class ProductInfoComponent implements OnInit {
  @Input() client: Client;
  @Input() currentGroup: ProductGroup;
  @Input() currentGroupNew: boolean = false;

  productGroupInfoForm: FormGroup;
  headerGroups = { productGroupInfo: true, productGroupDesign: true };
  brandingUpdates: boolean;
  /** Help text edited and not yet saved, by language code. See saveHelpReplies. */
  helpReplyEdits: { [languageCode: string]: string } = {};
  constructor(
    public newFeed: NewFeedTransitionService,
    private featureService: FeatureService,
    private smsLanguageConfigService: SmsLanguageConfigService,
  ) {}

  ngOnInit() {
    this.brandingUpdates = this.featureService.checkFlag('branding-updates');
    // form partially matches the ProductGroup Model in client.ts
    this.productGroupInfoForm = this.client.feed_enabled
      ? new FormGroup({
          name: new FormControl(this.currentGroup.name, [
            Validators.required,
            Validators.maxLength(
              CharacterLimits.ProductGroupFeedDisplayNameCharLimit,
            ),
          ]),
          internal_name: new FormControl(this.currentGroup.internal_name, [
            Validators.required,
            Validators.maxLength(256),
          ]),
          wire_header_name: new FormControl(this.currentGroup.wire_header_name),
          description: new FormControl(this.currentGroup.description, [
            Validators.maxLength(
              CharacterLimits.ProductGroupDescriptionCharLimit,
            ),
          ]),
          id: new FormControl(this.currentGroup.id, [
            Validators.required,
            CustomValidators.apiCompatibleID,
          ]),
          vanity_url: new FormControl(this.currentGroup.vanity_url, [
            CustomValidators.validURL,
          ]),
          is_vanity_url_enabled: new FormControl(
            this.currentGroup.is_vanity_url_enabled,
          ),
        })
      : new FormGroup({
          name: new FormControl(this.currentGroup.name, [Validators.required]),
          internal_name: new FormControl(this.currentGroup.internal_name, [
            Validators.required,
            Validators.maxLength(256),
          ]),
          wire_header_name: new FormControl(this.currentGroup.wire_header_name),
          description: new FormControl(this.currentGroup.description),
          id: new FormControl(this.currentGroup.id, [
            Validators.required,
            CustomValidators.apiCompatibleID,
          ]),
          vanity_url: new FormControl(
            this.currentGroup.vanity_url,
            CustomValidators.validURL,
          ),
          is_vanity_url_enabled: new FormControl(
            this.currentGroup.is_vanity_url_enabled,
          ),
        });

    this.productGroupInfoForm.valueChanges.subscribe(() => {
      this.currentGroup = _.assign(
        this.currentGroup,
        this.productGroupInfoForm.getRawValue(),
      );
    });

    const vanityUrl = this.productGroupInfoForm.get('vanity_url');
    vanityUrl.valueChanges
      .pipe(debounceTime(500))
      .subscribe((value: string) => {
        const sanitizedUrl = URLUtils.formatVanityURL(value);
        // prevent infinite loop
        vanityUrl.patchValue(sanitizedUrl, { emitEvent: false });
        // becuase this form decided to use two different models for some reason
        // they need to be kept in sync manually otherwise they update at different points in the
        // UI lifecycle leading to a delay. This is stupid and everything should just use the same model
        // but I don't have time to rewrite this entire page right now
        this.currentGroup.vanity_url = sanitizedUrl;
      });
  }

  // For Parent components using @ViewChild to access form data
  getData(): any {
    return this.productGroupInfoForm.getRawValue();
  }

  /**
   * Saves the help text edited since the page loaded. It isn't part of the
   * product group, so the page calls this once the product group has saved.
   */
  saveHelpReplies(productGroupId: string): Observable<unknown> {
    if (Object.keys(this.helpReplyEdits).length === 0) {
      return of(null);
    }
    return this.smsLanguageConfigService
      .saveProductGroupHelpReplies(this.client.id, productGroupId, this.helpReplyEdits)
      .pipe(tap(() => (this.helpReplyEdits = {})));
  }

  get vanityUrl(): string {
    return this.productGroupInfoForm.controls['vanity_url'].value;
  }

  get pgName(): boolean {
    return !this.currentGroup.hide_pg_wire_display_name;
  }

  onCheckboxModelChange(newValue: boolean): void {
    this.currentGroup.hide_pg_wire_display_name = !newValue;
  }

  toggleTitleBar(): void {
    this.currentGroup.show_title_bar =
      !this.currentGroup.show_title_bar;
  }

  toggleBanner(): void {
    this.currentGroup.show_banner =
      !this.currentGroup.show_banner;
  }

  toggleLogo(): void {
    this.currentGroup.show_logo =
      !this.currentGroup.show_logo;
  }

  setVanityUrl(event: any) {
    event.stopPropagation();
    this.productGroupInfoForm.controls['is_vanity_url_enabled'].setValue(
      !!this.vanityUrl && this.vanityUrl.length > 0,
    );
    this.currentGroup.vanity_url =
      this.productGroupInfoForm.controls['vanity_url'].value; // needed ONLY for the url preview
  }

  goToVanityURL(e): void {
    e.preventDefault();
    if (this.vanityUrl.indexOf('//') > -1) {
      window.open(this.vanityUrl);
    } else {
      window.open('//' + this.vanityUrl);
    }
  }

  getCharacterLimit(field: string): number {
    switch (field) {
      case 'feedDisplayName':
        return CharacterLimits.ProductGroupFeedDisplayNameCharLimit;
      case 'description':
        return CharacterLimits.ProductGroupDescriptionCharLimit;
      case 'customerContactsLabel':
        return CharacterLimits.ProductGroupCustomerContactsLabelCharLimit;
    }
  }
}
